import { ArrowDown, ArrowLeft } from '@tencent/odc-iconpack';
import { Empty, MenuList, Button } from 'odc-shared/components';
import OdcInput from 'odc-shared/components/Input/odcInput';
import React, { useCallback, useEffect, useMemo } from 'react';

import ArticlesCard from '@src/components/ArticlesCard';
import { getArticlePath } from '@src/utils/helpCenter';

import { PlatformCategory, SupportSearchPaginationState } from '../hooks';

interface SupportSearchProps {
  onBack: () => void;
  keyword: string;
  selectedCategory: PlatformCategory;
  searchState: SupportSearchPaginationState;
  onKeywordChange: (keyword: string) => void;
  onCategoryChange: (category: PlatformCategory) => void;
  onLoadMore: (platform: PlatformCategory) => void;
}

function SupportSearch({
  onBack,
  keyword,
  selectedCategory,
  searchState,
  onKeywordChange,
  onCategoryChange,
  onLoadMore,
}: SupportSearchProps) {
  // 当前选中平台的状态
  const currentPlatformState = useMemo(() => searchState[selectedCategory], [searchState, selectedCategory]);

  // 菜单数据 - 使用各平台的 totalCount
  const menuData = useMemo(() => {
    const result: { id: PlatformCategory; label: string }[] = [{
      id: 'all',
      label: `All results（${searchState.all.totalCount}）`,
    }];
    // 只有当平台有结果时才显示
    if (searchState.advertiser.totalCount > 0) {
      result.push({
        id: 'advertiser',
        label: `User growth（${searchState.advertiser.totalCount}）`,
      });
    }
    if (searchState.publisher.totalCount > 0) {
      result.push({
        id: 'publisher',
        label: `Monetization（${searchState.publisher.totalCount}）`,
      });
    }
    return result;
  }, [searchState.all.totalCount, searchState.publisher.totalCount, searchState.advertiser.totalCount]);

  // 处理搜索输入变化
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onKeywordChange(e.target.value);
  }, [onKeywordChange]);

  // 处理分类选择
  const handleCategoryChange = useCallback((categoryId: string | number | undefined) => {
    if (typeof categoryId === 'string') {
      onCategoryChange(categoryId as PlatformCategory);
    }
  }, [onCategoryChange]);

  // 处理加载更多
  const handleLoadMore = useCallback(() => {
    onLoadMore(selectedCategory);
  }, [onLoadMore, selectedCategory]);

  // 初始化时从 URL 读取 keyword
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('keyword')) {
      onKeywordChange(searchParams.get('keyword') || '');
      searchParams.delete('keyword');
      const newUrl = searchParams.toString()
        ? `${window.location.pathname}?${searchParams.toString()}`
        : window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [onKeywordChange]);

  return (
    <div className="odc-page-search-results">
      <div className="search-results-category">
        <MenuList
          data={menuData}
          value={selectedCategory}
          onChange={handleCategoryChange}
          useHoverFill={false}
        />
      </div>
      <div className="search-results-content">
        <div className='search-results-content-inner'>
          <div className="odc-page-back" onClick={onBack}>
            <ArrowLeft className="!mr-1r" />Back to home
          </div>
          <OdcInput
            search
            placeholder="Search by topic or keyword"
            value={keyword}
            onChange={handleSearchChange}
            className='!my-6r'
          />
          <div className="search-results-items">
            <div className="!gap-4r flex flex-col">
              {currentPlatformState.loading ? (
                // 首次加载中
                <div className="flex justify-center items-center py-8">
                  <span className="text-gray-500">Loading...</span>
                </div>
              ) : currentPlatformState.results.length > 0 ? (
                // 显示搜索结果
                <>
                  {currentPlatformState.results.map((post, index) => (
                    <ArticlesCard
                      key={post.id || index}
                      title={post.title || 'Untitled'}
                      url={`/supportcenter?pid=${post.id}&platform=${post.platform}`}
                      content={post.content || 'No description available'}
                      category={post.platform === 'publisher' ? 'Monetization' : 'User growth'}
                      path={getArticlePath(post.catalogs?.[0]?.path || [])}
                    />
                  ))}
                  {/* 加载更多按钮 */}
                  {currentPlatformState.hasMore && currentPlatformState.loadingMore && (
                    <div className="flex justify-center items-center py-8">
                      <span className="text-gray-500">Loading...</span>
                    </div>
                  )}
                  {currentPlatformState.hasMore && !currentPlatformState.loadingMore && (
                    <div className="load-more-wrapper">
                      <Button
                        onClick={handleLoadMore}
                        disabled={currentPlatformState.loadingMore}
                        size='sm'
                      >
                        View more
                        <ArrowDown className='!ml-1r text-base' />
                      </Button>
                    </div>
                  )}
                </>
              ) : keyword ? (
                // 搜索无结果
                <Empty
                  size="lg"
                  emptyTip="No quick matches found"
                  emptyDesc="Try checking for typos or search anyway"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportSearch;
